/**
 * featureFlags.js - Feature Flags Configuration
 * 
 * Centralized feature flag management for controlled rollout of new features.
 * All flags default to true in dev, with localStorage overrides for testing.
 * 
 * FLAGS:
 * - CLARITY_V2_ENABLED: Ambient simplification layer
 * - GIL_PROACTIVE_ENABLED: Proactive friction detection and nudges
 * - CONCEPT_ANIMS_ENABLED: Concept animation library
 * - CORPORATE_PROGRAMS_ENABLED: Corporate program governance templates
 * 
 * GOVERNANCE BOUNDARY:
 * - Flags control UX behavior only
 * - No execution or settlement logic behind flags
 */

const FLAG_KEYS = {
  CLARITY_V2: 'granullo_clarity_v2_enabled',
  GIL_PROACTIVE: 'granullo_gil_proactive_enabled',
  CONCEPT_ANIMS: 'granullo_concept_anims_enabled',
  CORPORATE_PROGRAMS: 'granullo_corporate_programs_enabled',
  VISUAL_PROOF: 'granullo_visual_proof_enabled',
  AB_TESTING: 'granullo_ab_testing_enabled',
  POST_SIGNUP_ACTIVATION: 'granullo_post_signup_activation_enabled',
  GRANULLO_INTELLIGENCE_LLM: 'granullo_intelligence_llm_enabled',
  SPEND_HEATMAPS: 'granullo_spend_heatmaps_enabled'
};

// Default values (all true in dev)
const DEFAULT_FLAGS = {
  [FLAG_KEYS.CLARITY_V2]: true,
  [FLAG_KEYS.GIL_PROACTIVE]: false, // Disabled until Phase 3 implementation verified
  [FLAG_KEYS.CONCEPT_ANIMS]: true,
  [FLAG_KEYS.CORPORATE_PROGRAMS]: true,
  [FLAG_KEYS.VISUAL_PROOF]: true,
  [FLAG_KEYS.AB_TESTING]: false, // Disabled by default, enable for testing
  [FLAG_KEYS.POST_SIGNUP_ACTIVATION]: true, // Enabled by default in dev/staging
  [FLAG_KEYS.GRANULLO_INTELLIGENCE_LLM]: true, // Enabled by default, falls back to seeded if API fails
  [FLAG_KEYS.SPEND_HEATMAPS]: false // Off by default - observational geographic visualization
};

/**
 * Get a feature flag value
 * Priority: localStorage > env > default
 */
export const getFlag = (flagKey) => {
  if (typeof window === 'undefined') {
    return DEFAULT_FLAGS[flagKey] ?? false;
  }

  // Check localStorage override first
  const localValue = localStorage.getItem(flagKey);
  if (localValue !== null) {
    return localValue === 'true';
  }

  // Check environment variable (via process.env)
  const envKey = flagKey.toUpperCase().replace('granullo_', '').replace(/_/g, '_');
  const envValue = process.env[`REACT_APP_${envKey}`];
  if (envValue !== undefined) {
    return envValue === 'true';
  }

  // Return default
  return DEFAULT_FLAGS[flagKey] ?? false;
};

/**
 * Set a feature flag value (localStorage only)
 */
export const setFlag = (flagKey, value) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem(flagKey, String(value));
  }
};

/**
 * Clear all feature flag overrides
 */
export const clearAllFlags = () => {
  if (typeof window !== 'undefined') {
    Object.values(FLAG_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }
};

/**
 * Get all flag states
 */
export const getAllFlags = () => ({
  clarityV2: getFlag(FLAG_KEYS.CLARITY_V2),
  gilProactive: getFlag(FLAG_KEYS.GIL_PROACTIVE),
  conceptAnims: getFlag(FLAG_KEYS.CONCEPT_ANIMS),
  corporatePrograms: getFlag(FLAG_KEYS.CORPORATE_PROGRAMS),
  visualProof: getFlag(FLAG_KEYS.VISUAL_PROOF),
  abTesting: getFlag(FLAG_KEYS.AB_TESTING),
  postSignupActivation: getFlag(FLAG_KEYS.POST_SIGNUP_ACTIVATION),
  granulloIntelligenceLlm: getFlag(FLAG_KEYS.GRANULLO_INTELLIGENCE_LLM),
  spendHeatmaps: getFlag(FLAG_KEYS.SPEND_HEATMAPS)
});

// Named exports for specific flags
export const isClarityV2Enabled = () => getFlag(FLAG_KEYS.CLARITY_V2);
export const isGilProactiveEnabled = () => getFlag(FLAG_KEYS.GIL_PROACTIVE);
export const isConceptAnimsEnabled = () => getFlag(FLAG_KEYS.CONCEPT_ANIMS);
export const isCorporateProgramsEnabled = () => getFlag(FLAG_KEYS.CORPORATE_PROGRAMS);
export const isVisualProofEnabled = () => getFlag(FLAG_KEYS.VISUAL_PROOF);
export const isAbTestingEnabled = () => getFlag(FLAG_KEYS.AB_TESTING);
export const isPostSignupActivationEnabled = () => getFlag(FLAG_KEYS.POST_SIGNUP_ACTIVATION);
export const isGranulloIntelligenceLlmEnabled = () => getFlag(FLAG_KEYS.GRANULLO_INTELLIGENCE_LLM);
export const isSpendHeatmapsEnabled = () => getFlag(FLAG_KEYS.SPEND_HEATMAPS);

// Export flag keys for external use
export { FLAG_KEYS };

export default {
  FLAG_KEYS,
  getFlag,
  setFlag,
  clearAllFlags,
  getAllFlags,
  isClarityV2Enabled,
  isGilProactiveEnabled,
  isConceptAnimsEnabled,
  isCorporateProgramsEnabled,
  isVisualProofEnabled,
  isAbTestingEnabled,
  isPostSignupActivationEnabled,
  isSpendHeatmapsEnabled
};
